import React, { useEffect, useRef } from 'react'
import FlatListElementWrapper from '../atoms/FlatListElementWrapper'
import { useSelector } from 'react-redux'
import { RootReducer } from 'src/redux/rootReducer'
// page size should be higher than number of elements per row

interface Props {
  data: any[]
  component: React.FC<any>
  fetchMore: () => Promise<void>
  itemMinWidth?: string
  filters?: any
}

const FetchMoreThreshold = 200

const FlatList = ({ data, component, fetchMore, itemMinWidth = '300px', filters }: Props): JSX.Element => {
  const reRenderFlatList = useSelector((state: RootReducer) => state.tableToolsReducer.rerenderFlatList)
  const scrollHeightRef = useRef<number>(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const observer = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // it is emergency mechanism in case that logic with scroll detection will not work,
    // it is based on observer, so if div is visible beneath list it means that content needs to be fetched
    if (sentinelRef.current === null || scrollRef.current === null) return

    observer.current?.disconnect()

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchMore().catch(() => null)
        }
      },
      {
        root: scrollRef.current,
        rootMargin: '0px',
        threshold: 0.1
      }
    )

    observer.current.observe(sentinelRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    scrollHeightRef.current = 0
  }, [itemMinWidth])

  useEffect(() => {
    scrollHeightRef.current = 0
  }, [reRenderFlatList])

  useEffect(() => {
    scrollHeightRef.current = 0
  }, [filters])

  const handleScroll = (): void => {
    if (scrollRef.current !== null) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
      if (scrollHeight - scrollTop - FetchMoreThreshold < clientHeight && scrollHeight > scrollHeightRef.current) {
        scrollHeightRef.current = scrollHeight
        fetchMore().catch(() => null)
      }
    }
  }

  // useEffect(() => {
  //   document.body.style.overflow = 'hidden'
  //   return () => {
  //     document.body.style.overflow = ''
  //   }
  // }, [data])

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="h-full overflow-y-scroll grid auto-rows-auto hide-scrollbar"
      style={{
        gridTemplateColumns: `repeat(auto-fill, minmax(${itemMinWidth}, 1fr))`,
        display: itemMinWidth === '100%' ? 'flex' : 'grid',
        flexDirection: 'column'
      }}>
      {data.map((item, idx) => (
        <FlatListElementWrapper key={item.id} index={idx} component={component} item={item} />
      ))}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  )
}

export default FlatList
